import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, Tabs, Tab, Alert } from '@mui/material'
import { Add, ShoppingCartOutlined } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listStockBalances, listStockAvailability, type StockBalance, type StockAvailability } from '../../api/stocuri'
import StockAdjustmentDialog from './StockAdjustmentDialog'
import PurchaseRequestFormDialog, { type PurchaseRequestPrefill } from '../achizitii/PurchaseRequestFormDialog'

export default function StockPage() {
  const [tab, setTab] = useState<'sold' | 'disponibil'>('disponibil')
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [requestPrefill, setRequestPrefill] = useState<PurchaseRequestPrefill | null>(null)
  const { data: balances, isLoading: loadingBalances } = useQuery({ queryKey: ['stocuri', 'stoc'], queryFn: () => listStockBalances() })
  const { data: availability, isLoading: loadingAvailability } = useQuery({ queryKey: ['stocuri', 'stoc', 'disponibil'], queryFn: listStockAvailability })

  // FR-ACA-003/FR-INV-007 — propune cererea de achiziție pentru articolele sub stocul minim, fără reintroducere de date.
  const generatePurchaseRequestFromAlerts = (rows: StockAvailability[]) => setRequestPrefill({
    notes: 'Generat automat din alerta de stoc sub minim.',
    lines: rows.map((r) => ({
      articleId: r.articleId, description: `Sub stoc minim în ${r.warehouseName} (disponibil ${r.availableQuantity.toFixed(3).replace('.', ',')}, minim ${r.minStockQuantity?.toFixed(3).replace('.', ',')})`,
      quantityRequested: String((r.minStockQuantity ?? 0) - r.availableQuantity), unitOfMeasureId: r.unitOfMeasureId,
    })),
  })

  const balanceColumns = useMemo<GridColDef<StockBalance>[]>(() => [
    { field: 'articleCode', headerName: 'Cod', width: 130 },
    { field: 'articleDescription', headerName: 'Articol', flex: 1, minWidth: 180 },
    { field: 'warehouseName', headerName: 'Depozit', width: 160 },
    { field: 'zoneName', headerName: 'Zonă', width: 130, valueGetter: (v: string | null) => v ?? '' },
    { field: 'locationCode', headerName: 'Locație', width: 110, valueGetter: (v: string | null) => v ?? '' },
    { field: 'lotNumber', headerName: 'Lot', width: 130, valueGetter: (v: string | null) => v ?? '' },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (p) => <Chip size="small" label={p.row.status} color={p.row.status === 'Liber' ? 'success' : p.row.status === 'Blocat' ? 'error' : 'warning'} variant="outlined" />,
    },
    { field: 'quantity', headerName: 'Cantitate', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(3).replace('.', ',') },
  ], [])

  const availabilityColumns = useMemo<GridColDef<StockAvailability>[]>(() => [
    { field: 'articleCode', headerName: 'Cod', width: 130 },
    { field: 'articleDescription', headerName: 'Articol', flex: 1, minWidth: 180 },
    { field: 'warehouseName', headerName: 'Depozit', width: 160 },
    { field: 'liberQuantity', headerName: 'Liber', width: 100, type: 'number', valueFormatter: (v: number) => v.toFixed(3).replace('.', ',') },
    { field: 'reservedQuantity', headerName: 'Rezervat', width: 100, type: 'number', valueFormatter: (v: number) => v.toFixed(3).replace('.', ',') },
    { field: 'availableQuantity', headerName: 'Disponibil', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(3).replace('.', ',') },
    { field: 'minStockQuantity', headerName: 'Stoc minim', width: 110, valueGetter: (v: number | null) => v ?? '' },
    {
      field: 'isBelowMinStock', headerName: '', width: 130, sortable: false,
      renderCell: (p) => p.row.isBelowMinStock ? <Chip size="small" label="Sub minim" color="warning" /> : null,
    },
  ], [])

  const belowMinCount = (availability ?? []).filter((a) => a.isBelowMinStock).length

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Stocuri</Typography>
        <Typography variant="inherit" color="text.primary">Stoc curent</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Stoc curent</Typography>
      {belowMinCount > 0 && (
        <Alert severity="warning" action={
          <Button color="inherit" size="small" startIcon={<ShoppingCartOutlined fontSize="small" />}
            onClick={() => generatePurchaseRequestFromAlerts((availability ?? []).filter((a) => a.isBelowMinStock))}>
            Cerere achiziție
          </Button>
        }>
          {belowMinCount} articol(e) sub stocul minim (FR-INV-007).
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="disponibil" label="Disponibil per articol×depozit" />
          <Tab value="sold" label="Sold detaliat (zonă/lot/status)" />
        </Tabs>
        <Button variant="contained" startIcon={<Add />} onClick={() => setAdjustOpen(true)}>Ajustare manuală</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        {tab === 'disponibil' ? (
          <DataGrid rows={availability ?? []} columns={availabilityColumns} loading={loadingAvailability} density="comfortable"
            disableRowSelectionOnClick getRowId={(r) => `${r.articleId}-${r.warehouseId}`}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
        ) : (
          <DataGrid rows={balances ?? []} columns={balanceColumns} loading={loadingBalances} density="comfortable"
            disableRowSelectionOnClick getRowId={(r) => `${r.articleId}-${r.warehouseId}-${r.zoneId}-${r.locationId}-${r.lotNumber}-${r.status}`}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
        )}
      </Box>

      <StockAdjustmentDialog open={adjustOpen} onClose={() => setAdjustOpen(false)} />
      <PurchaseRequestFormDialog target={requestPrefill ? 'new' : null} prefill={requestPrefill} onClose={() => setRequestPrefill(null)} />
    </Stack>
  )
}
